import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, ArrowRight, ChevronDown, ChevronRight,
  AlertTriangle, Wrench, PauseCircle, PlayCircle, CheckCircle2, RotateCcw,
  XCircle, IndianRupee, CalendarClock, Package, ClipboardList, Bell, FileEdit,
} from "lucide-react";
import { MAINT_STAGE_BY_KEY } from "@/components/maintenance/lib";

// Live Activity · Maintenance — the ERP feed's anatomy, verbatim
// (components/dashboard/LiveUpdatesCard.js): a header that stays put, a
// scrolling feed of COMPACT rows, and a footer link out. Each row is three
// tight lines rather than a paragraph:
//
//   [REPAIR] ×2 ······················· Tushar · 20m     ← who and when
//   Reported → Hydraulic leak / low pressure             ← label spine, one pill
//   PUMA 800XLY II  [ME303012]              BD-0007      ← what it was about
//
// Consecutive entries from the same person doing the same thing collapse into
// one row with a ×N badge, so a burst of five spare updates costs one row, not
// five. The card owns its height and scrolls inside — it can never push the
// dashboard down again.

const TONE_CLASS = {
  green:   "rl-pill border border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400",
  red:     "rl-pill border border-red-500/40 bg-red-500/5 text-red-700 dark:text-red-400",
  amber:   "rl-pill border border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-400",
  blue:    "rl-pill border border-blue-500/40 bg-blue-500/5 text-blue-700 dark:text-blue-400",
  teal:    "rl-pill border border-teal-500/40 bg-teal-500/5 text-teal-700 dark:text-teal-400",
  neutral: "border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 text-[hsl(var(--foreground))]/80",
};

// Every action the module writes to the changelog, with the word that goes in
// the label spine. Keep this in step with the _maint_log calls on the server.
const ACTION_VISUAL = {
  "Breakdown Reported":   { label: "Reported",   icon: AlertTriangle,  tone: "red" },
  "Repair Taken Up":      { label: "Taken up",   icon: Wrench,         tone: "blue" },
  "Repair Paused":        { label: "Paused",     icon: PauseCircle,    tone: "amber" },
  "Repair Resumed":       { label: "Resumed",    icon: PlayCircle,     tone: "blue" },
  "Marked Repaired":      { label: "Repaired",   icon: CheckCircle2,   tone: "green" },
  "Handback Confirmed":   { label: "Signed back", icon: CheckCircle2,  tone: "green" },
  "Sent Back to Repair":  { label: "Sent back",  icon: RotateCcw,      tone: "teal" },
  "Report Withdrawn":     { label: "Withdrawn",  icon: XCircle,        tone: "neutral" },
  "Machine Rate Set":     { label: "₹/hr rate",  icon: IndianRupee,    tone: "neutral" },
  "PM Service Logged":    { label: "PM service", icon: CalendarClock,  tone: "green" },
  "PM Interval Set":      { label: "PM every",   icon: CalendarClock,  tone: "neutral" },
  "Spare Order Logged":   { label: "Spare",      icon: Package,        tone: "blue" },
  "Spare Order Updated":  { label: "Spare",      icon: Package,        tone: "blue" },
  "Request Raised":       { label: "Request",    icon: ClipboardList,  tone: "blue" },
  "Pending Point Raised": { label: "Pending pt", icon: ClipboardList,  tone: "amber" },
  "Request Taken Up":     { label: "Taken up",   icon: Wrench,         tone: "blue" },
  "Request Resolved":     { label: "Resolved",   icon: CheckCircle2,   tone: "green" },
  "Request Reopened":     { label: "Reopened",   icon: RotateCcw,      tone: "amber" },
  "Alert Settings Changed": { label: "Alerts",   icon: Bell,           tone: "neutral" },
};
const visualFor = (action) => ACTION_VISUAL[action] || { label: action || "Changed", icon: FileEdit, tone: "neutral" };

function timeAgo(ts) {
  const s = (Date.now() - new Date(ts)) / 1000;
  if (s < 5) return "just now";
  if (s < 60) return `${Math.floor(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 10800) return `${Math.floor(s / 3600)}h`;
  const d = new Date(ts);
  if (d.toDateString() === new Date().toDateString())
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${String(d.getDate()).padStart(2, "0")}-${d.toLocaleString("en-US", { month: "short" })}`;
}

const shortName = (user) => (user ? user.replace(/\s*\(.*\)/, "").trim().split(" ")[0] : "?");

// The left-hand chip. A breakdown carries its PIPELINE STAGE; everything else
// carries what it is — PM, a spare, a request, a setting. Nothing gets a
// "Complete" pill just because it has no ticket behind it.
const FAMILY = { PM: "PM", SP: "Spare", RQ: "Request" };
function familyOf(row) {
  const id = String(row.ticket_id || "");
  if (id.startsWith("BD")) return null;
  return FAMILY[id.slice(0, 2)] || "Setup";
}

function StagePill({ row }) {
  const family = familyOf(row);
  if (family) {
    return (
      <span className="inline-flex items-center justify-center text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded-full border rl-pill leading-none whitespace-nowrap border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 text-[hsl(var(--muted-foreground))]">
        {family}
      </span>
    );
  }
  const st = MAINT_STAGE_BY_KEY[row.stage] || MAINT_STAGE_BY_KEY.complete;
  return (
    <span
      className="inline-flex items-center justify-center text-[10px] font-semibold tracking-wide px-2 py-0.5 rounded-full border rl-pill leading-none whitespace-nowrap"
      style={{ background: `${st.color}0d`, color: st.color, borderColor: `${st.color}59` }}
    >
      {st.short}
    </span>
  );
}

/* The ledger line: quiet right-aligned label column → arrow → the detail as
   the hero pill, so every arrow lines up down the feed. */
function ChangeLine({ action, detail, suffix }) {
  const v = visualFor(action);
  const Icon = v.icon;
  const text = (detail || "").trim();
  return (
    <div className="flex items-center gap-1.5 leading-tight min-w-0">
      <span className="w-[74px] shrink-0 text-right text-[11px] font-medium text-[hsl(var(--foreground))]/80 truncate" title={action}>{v.label}</span>
      <ArrowRight className="w-2.5 h-2.5 shrink-0 text-[hsl(var(--muted-foreground))]/40" aria-hidden="true" />
      <span
        title={text || action}
        className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-px rounded-full leading-[18px] min-w-0 ${TONE_CLASS[v.tone]}`}
      >
        <Icon className="w-3 h-3 shrink-0" />
        <span className="truncate">{text || v.label}</span>
      </span>
      {suffix}
    </div>
  );
}

/* The machine line — identity + the breakdown it belongs to, clickable
   through to the stage that ticket is sitting in. */
function MachineMeta({ row, onOpen }) {
  if (!row.machine && !row.machine_no) return null;
  return (
    <button
      type="button"
      onClick={() => onOpen(row)}
      title="Open the stage this belongs to"
      data-testid={`mt-activity-link-${row.id}`}
      className="grid w-full text-left items-center gap-2 rounded px-1 -mx-1 py-px hover:bg-orange-500/10 transition-colors group/meta"
      style={{ gridTemplateColumns: "minmax(0,1fr) auto auto" }}
    >
      <span className="text-[11px] text-[hsl(var(--muted-foreground))] truncate group-hover/meta:text-orange-600">{row.machine || "—"}</span>
      {row.machine_no
        ? <span className="rl-me-chip rl-carved justify-self-start group-hover/meta:text-orange-600">{row.machine_no}</span>
        : <span />}
      <span className="text-[10px] font-mono-data text-[hsl(var(--muted-foreground))]/70 whitespace-nowrap justify-self-end group-hover/meta:text-orange-600">
        {row.ticket_id || ""}
      </span>
    </button>
  );
}

// Same person, same action, within a quarter of an hour = one row.
const GROUP_WINDOW_MS = 15 * 60 * 1000;
function buildGroups(rows) {
  const out = [];
  for (const r of rows) {
    const last = out[out.length - 1];
    const prev = last?.[last.length - 1];
    const near = prev && Math.abs(new Date(prev.timestamp) - new Date(r.timestamp)) <= GROUP_WINDOW_MS;
    if (prev && prev.user === r.user && prev.action === r.action && near) last.push(r);
    else out.push([r]);
  }
  return out;
}

export default function MaintenanceActivityCard({ activity, className = "" }) {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState({});
  const groups = useMemo(() => buildGroups(activity || []), [activity]);

  const open = useCallback((row) => {
    const id = String(row.ticket_id || "");
    if (id.startsWith("PM")) return navigate("/maintenance/pm");
    if (id.startsWith("SP")) return navigate("/maintenance/spares");
    if (id.startsWith("RQ")) return navigate("/maintenance/requests");
    const st = MAINT_STAGE_BY_KEY[row.stage];
    navigate(st ? st.path : "/maintenance/complete");
  }, [navigate]);

  return (
    <div
      className={`rl-glass-edge rounded-[14px] bg-[hsl(var(--card))] flex flex-col min-h-0 overflow-hidden ${className}`}
      data-testid="mt-live-activity"
    >
      <div className="flex items-center gap-2 px-[18px] pt-4 pb-2.5 shrink-0 border-b border-[hsl(var(--border))]/40">
        <Activity className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
        <h3 className="text-[11px] font-bold uppercase tracking-[0.08em] text-[hsl(var(--foreground))]/85">
          Live Activity <span className="opacity-50">·</span> Maintenance
        </h3>
        <span className="ml-auto w-[7px] h-[7px] rounded-full bg-[hsl(var(--primary))] shadow-[0_0_6px_hsl(var(--primary)/0.7)]" />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[hsl(var(--border))] [&::-webkit-scrollbar-track]:bg-transparent">
        {groups.length === 0 && (
          <p className="text-[12px] text-[hsl(var(--muted-foreground))] px-[18px] py-5 leading-relaxed">
            Every report, repair and sign-off shows up here as it happens.
          </p>
        )}
        {groups.map((group, gi) => {
          const latest = group[0];
          const many = group.length > 1;
          const key = latest.id || `g-${gi}`;
          const isOpen = !collapsed[key];
          const prev = gi > 0 ? groups[gi - 1][0] : null;
          const gap = prev && (new Date(prev.timestamp) - new Date(latest.timestamp)) > 3600000;
          return (
            <React.Fragment key={key}>
              {gap && (
                <div className="flex items-center gap-2 px-4 py-1">
                  <div className="flex-1 h-px bg-[hsl(var(--border))]" />
                  <span className="text-[8px] font-medium uppercase tracking-widest text-[hsl(var(--muted-foreground))]/50 shrink-0">earlier</span>
                  <div className="flex-1 h-px bg-[hsl(var(--border))]" />
                </div>
              )}
              <div className="px-3 py-1 border-b border-[hsl(var(--border))]/40 last:border-0 hover:bg-[hsl(var(--muted))]/20 transition-colors">
                <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                  <StagePill row={latest} />
                  {many && <span className="text-[10px] font-mono-data text-[hsl(var(--muted-foreground))]/70 leading-none">×{group.length}</span>}
                  <span className="ml-auto flex items-baseline gap-1.5 whitespace-nowrap shrink-0">
                    <span className="text-[12px] font-semibold">{shortName(latest.user)}</span>
                    <span className="text-[11px] font-mono-data text-[hsl(var(--muted-foreground))]">{timeAgo(latest.timestamp)}</span>
                  </span>
                </div>

                <ChangeLine action={latest.action} detail={latest.detail} />

                {many ? (
                  <div className="mt-0.5">
                    {isOpen && group.map((r) => <MachineMeta key={r.id} row={r} onOpen={open} />)}
                    <button
                      type="button"
                      onClick={() => setCollapsed((c) => ({ ...c, [key]: isOpen }))}
                      data-testid={`mt-activity-group-${gi}`}
                      className="flex items-center gap-1 text-[10px] font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors mt-0.5"
                    >
                      {isOpen ? <ChevronDown className="w-3 h-3 shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />}
                      {isOpen ? "Hide" : "Show"} {group.length} machines
                    </button>
                  </div>
                ) : (
                  <MachineMeta row={latest} onOpen={open} />
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <div className="px-3 pb-1.5 pt-1 shrink-0 border-t border-[hsl(var(--border))]/40">
        <button
          type="button"
          onClick={() => navigate("/maintenance/complete")}
          data-testid="mt-activity-view-all"
          className="w-full flex items-center justify-center gap-1.5 py-1 rounded-md text-[9px] font-semibold uppercase tracking-wider text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/50 transition-colors"
        >
          The permanent record
          <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
